import React from 'react'
import styled from 'styled-components'
import { Link } from 'gatsby'
import LazyLoad from 'react-lazyload'
import { rem, lighten } from 'polished'
import media, {
  lazyload,
  CategoryBadge,
  VisuallyHidden,
  Absolute
} from '../../utils/style'
import { Flex, Box } from 'rebass'
import { Heading } from 'rebass'
import citygateTheme from '../../theme/'
import NumberFormat from 'react-number-format'
import IconHeart from '../../vectors/IconHeart'
import BannerVehicleOnOffer from '../../vectors/BannerVehicleOnOffer'
import typography from '../../utils/typography'
import { InfoButton } from '../ContentInfoModal'
import FinanceModal from '../FinanceModal'
import { func, string, bool, number, shape, object } from 'prop-types'
import { withFavouritesContext } from '../../context/favourites'

/*************************
 STYLES: Card
 **************************/

const Card = styled(Flex)`
  position: relative;
  z-index: 0;
  background-color: white;
  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    bottom: 1.5%;
    left: 50%;
    width: 94%;
    height: 50px;
    transform: translate(-50%, 0);
    box-shadow: 0 18px 18px 0 rgba(66, 76, 94, 0.04),
      0 5px 5px 0 rgba(0, 0, 0, 0.06);
  }
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
    width: 100%;
    height: 100%;
    background: white;
  }
`

const CardImage = styled(Box)`
  position: relative;
`

const VehiclePreviewImage = styled.div`
  position: relative;
  height: 100%;
  padding-top: 64.07%;
  background-image: linear-gradient(-180deg, #c6cad2 49%, #959cac 53%);
  &::after {
    content: '';
    position: absolute;
    z-index: 21;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    min-height: 100%;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    background-image: url(${(props) => props.bg});
  }
`

/*************************
 STYLES: Detail
 **************************/

const CardDetail = styled(Box)`
  position: relative;
  background-color: white;
  min-height: 100%;
  display: flex;
  flex-direction: column;
`

const VehicleSummary = styled(Box)`
  flex: 1 0 auto;
`

const VehicleMakeAndModel = styled(Heading)`
  color: ${(props) => props.highlight || props.theme.colors.blue};
  font-size: ${(props) => rem(props.theme.fontSizes[3])};
  ${media.MediumScreenUp`
    font-size: ${(props) => rem(props.theme.fontSizes[5])};
  `};
`

VehicleMakeAndModel.displayName = 'VehicleMakeAndModel'

const VehicleEngine = styled(Heading)`
  display: block;
  font-size: ${(props) => rem(props.theme.fontSizes[2])};
  ${media.MediumScreenUp`
    font-size: ${(props) => rem(props.theme.fontSizes[4])};
  `};
  font-weight: ${(props) => props.theme.weights[0]};
  color: ${(props) => props.theme.colors.grey_light};
`

VehicleEngine.displayName = 'VehicleEngine'

const VehicleOfferTitle = styled.div`
  font-size: 20px;
  font-weight: 500;
  line-height: 1.2;
  padding-bottom: 2px;
  color: ${(props) => props.theme.colors.green};
`

const VehicleOfferDescription = styled.div`
  font-size: 16px;
  color: ${(props) => props.theme.colors.blue};
  line-height: 1.2;
`

const VehicleMiscDetails = styled.div`
  font-size: ${(props) => rem(props.theme.fontSizes[1])};
  ${media.MediumScreenUp`
    font-size: ${rem(15)};
  `};
  line-height: 1.6;
  color: ${(props) => props.theme.colors.grey_op80};
  span > span {
    color: ${(props) => props.highlight || props.theme.colors.blue};
    display: inline-block;
    padding: 0 12px;
  }
`

/*************************
 STYLES: Price
 **************************/

const PriceSummary = styled(Flex)`
  color: ${(props) => props.theme.colors.grey_light};
  font-size: ${(props) => rem(props.theme.fontSizes[2])};
  line-height: 1.2;
  box-shadow: 0 18px 18px 0 rgba(66, 76, 94, 0.05),
    0 5px 5px 0 rgba(0, 0, 0, 0.07);
  background-color: ${(props) => props.theme.colors.blue_light};
  ${media.LargeScreenUp`
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  `};
  ${media.GiantScreenUp`
    font-size: ${(props) => rem(props.theme.fontSizes[3])};
  `};
`

PriceSummary.displayName = 'PriceSummary'

const Price = styled.span`
  display: inline-block;
  color: ${(props) => props.highlight || props.theme.colors.blue};
  font-weight: ${(props) => props.theme.weights[1]};
  font-size: ${(props) => rem(props.theme.fontSizes[4])};
  text-transform: uppercase;
  ${media.GiantScreenUp`
    font-size: ${(props) => rem(props.theme.fontSizes[5])};
  `};
`

Price.displayName = 'Price'

const Favourite = styled(Absolute)`
  z-index: 40;
  svg {
    transition: transform 0.2s ${(props) => props.theme.ease.ease_out_back};
    transform-origin: 50%;
  }
  path {
    transition: fill 0.5s ${(props) => props.theme.ease.ease_out_expo};
    transform-origin: 50%;
  }
  path:last-child {
    fill: ${(props) => props.theme.colors.grey_op40};
  }
  path:first-child {
    transform: scale(1.1) translateZ(0);
  }
  &:hover path:last-child {
    fill: ${(props) => props.theme.colors.blue};
  }
  & .favourite svg {
    transform: scale(1.18) translateZ(0);
    path {
      fill: ${(props) => props.theme.colors.blue};
    }
  }
  &:hover .favourite svg {
    transform: scale(1.15) translateZ(0);
    path {
      fill: ${(props) => lighten(0.1, props.theme.colors.blue)};
    }
  }
`

Favourite.displayName = 'Favourite'

const VehicleViewDetails = styled(Box)`
  text-align: center;
  position: relative;
  background-color: ${(props) => props.highlight || props.theme.colors.blue};
  transition: background-color 0.5s ${(props) => props.theme.ease.ease_out_expo};
  min-height: ${typography.rhythm(1.3)};
  display: inline-block;
  color: white;
  text-decoration: none;
  text-transform: uppercase;
  font-weight: ${(props) => props.theme.weights[1]};
  font-size: ${(props) => rem(props.theme.fontSizes[1])};
  &:hover {
    background-color: ${(props) =>
      lighten(0.1, props.highlight || props.theme.colors.blue)};
  }
  span {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`

VehicleViewDetails.displayName = 'VehicleViewDetails'

const BannerVehicleOnOfferStripe = styled(BannerVehicleOnOffer)`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 25;
`
BannerVehicleOnOfferStripe.displayName = 'BannerVehicleOnOfferStripe'

const Badges = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  padding-right: 25%;
  z-index: 25;
`

const RecentBadge = styled(CategoryBadge)`
  float: left;
  background-color: ${(props) => props.theme.colors.grey};
`
RecentBadge.displayName = 'RecentBadge'

const ReservedBadge = styled(CategoryBadge)`
  float: left;
`
ReservedBadge.displayName = 'ReservedBadge'

const LinkOverlay = styled(Link)`
  position: absolute;
  background: rgba(0, 0, 0, 0); /* IE hit-area fix */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;

  &:hover
    ~ ${/* sc-selector */ CardDetail}
    ${/* sc-selector */ VehicleViewDetails} {
    background-color: ${(props) =>
      lighten(0.1, props.highlight || props.theme.colors.blue)};
  }
`

const HiddenLinkLabel = styled.span`
  ${VisuallyHidden}
`

class VehicleListingFilterCard extends React.PureComponent {
  constructor(props) {
    super(props)

    this.toggleFavourite = this.toggleFavourite.bind(this)
  }

  toggleFavourite(e) {
    if (e) e.preventDefault()
    this.props.context.toggle(this.props.id)
  }

  render() {
    const priceData = this.props.price
    const highlightColour = citygateTheme.colors.blue
    const isSold = this.props.isSold || !!this.props.expiredDate
    const { offerTitle, offerDescription } = this.props
    const hasOfferText = offerTitle || offerDescription

    return (
      <Card
        flexWrap="wrap"
        mb={32}
        as="article"
        vocab="http://schema.org/"
        typeof="Product"
      >
        <LinkOverlay
          highlight={highlightColour}
          to={this.props.path}
          property="url"
        >
          <HiddenLinkLabel>
            {this.props.brand} {this.props.model}
          </HiddenLinkLabel>
        </LinkOverlay>
        <CardImage width={['100%', null, 7 / 16, 6 / 16]}>
          <LazyLoad offset={lazyload.offset} height={200} once>
            <VehiclePreviewImage
              my={0}
              bg={this.props.image}
              ext={`${this.props.ext}`}
              property="image"
            />
          </LazyLoad>

          {this.props.offer && <BannerVehicleOnOfferStripe />}
          <Badges>
            {this.props.reserved && (
              <ReservedBadge mb={5} mr={0}>
                reserved
              </ReservedBadge>
            )}
            {this.props.recent && (
              <RecentBadge mb={5} mr={0}>
                just added
              </RecentBadge>
            )}
            {this.props.vatexempt && (
              <RecentBadge mb={5} mr={0}>
                VAT exempt
              </RecentBadge>
            )}
          </Badges>
        </CardImage>
        <CardDetail width={['100%', null, 9 / 16, 10 / 16]}>
          <VehicleSummary px={'7%'} pt={'6%'} pb={[30, '6%']}>
            <Box as="header" mb={[20, 35]}>
              <VehicleMakeAndModel
                as="h4"
                property="name"
                mb="8px"
                pr={30}
                highlight={highlightColour}
                aria-hidden="true"
              >
                {this.props.brand} {this.props.model}
              </VehicleMakeAndModel>
              <VehicleEngine mb={2}>{this.props.derivative}</VehicleEngine>
              {hasOfferText && (
                <Flex flexDirection="column">
                  {offerTitle && (
                    <VehicleOfferTitle>{offerTitle}</VehicleOfferTitle>
                  )}
                  {offerDescription && (
                    <VehicleOfferDescription>
                      {offerDescription}
                    </VehicleOfferDescription>
                  )}
                </Flex>
              )}
            </Box>

            <VehicleMiscDetails property="description">
              {this.props.details.map((detail, index) => (
                <span key={index}>
                  {detail}
                  {index !== this.props.details.length - 1 && <span> | </span>}
                </span>
              ))}
            </VehicleMiscDetails>
            <Favourite top right mr={'7%'} mt={'7%'}>
              <a
                href="javascript:void(0)"
                aria-label="Favourite"
                onClick={this.toggleFavourite}
                className={
                  this.props.context.isFavourited(this.props.id)
                    ? 'favourite'
                    : null
                }
              >
                <IconHeart />
              </a>
            </Favourite>
          </VehicleSummary>
          <PriceSummary flexWrap="wrap" property="offers" typeof="Offer">
            {!isSold && (
              <Box width={[10 / 10, 6 / 10]} px={'7%'} py={'13px'}>
                <Price highlight={highlightColour}>
                  {this.props.finance &&
                    this.props.finance.monthlyPayment &&
                    this.props.onInfoOpen && (
                      <InfoButton
                        secondary
                        onClick={() => {
                          this.props.onInfoOpen({
                            title: `Representative Finance Example (${priceData.installment.type.toUpperCase()})`,
                            children: (
                              <FinanceModal
                                symbol={this.props.currency.symbol}
                                {...this.props.finance}
                              />
                            )
                          })
                        }}
                      />
                    )}
                  <span
                    property="priceCurrency"
                    content={this.props.currency.code}
                  />
                  <span property="price" content={priceData.total}>
                    {this.props.offer && <span>Now </span>}
                    <NumberFormat
                      value={priceData.total}
                      displayType={'text'}
                      thousandSeparator
                      prefix={this.props.currency.symbol}
                    />
                  </span>
                </Price>
                {priceData.installment.payment && (
                  <span
                    id="price-installments"
                    itemProp="priceSpecification"
                    itemScope
                    itemType="http://schema.org/UnitPriceSpecification"
                  >
                    &nbsp;&nbsp;or from&nbsp;
                    <span
                      property="priceCurrency"
                      content={this.props.currency.code}
                    />
                    <span
                      property="price"
                      content={priceData.installment.payment}
                    >
                      <NumberFormat
                        value={priceData.installment.payment}
                        displayType={'text'}
                        thousandSeparator
                        prefix={this.props.currency.symbol}
                        decimalScale={2}
                        fixedDecimalScale
                      />
                    </span>
                    &nbsp;/&nbsp;
                    <span
                      itemProp="unitCode"
                      content={priceData.installment.code}
                    >
                      {priceData.installment.plan} (
                      {priceData.installment.type.toUpperCase()})
                    </span>
                  </span>
                )}
              </Box>
            )}

            {isSold && (
              <Box width={[10 / 10, 6 / 10]} px={'7%'} py={'13px'}>
                <Price highlight={highlightColour}>Vehicle just sold</Price>
              </Box>
            )}

            <VehicleViewDetails
              width={[10 / 10, 4 / 10]}
              highlight={highlightColour}
            >
              <span>View&nbsp;details</span>
            </VehicleViewDetails>
          </PriceSummary>
        </CardDetail>
      </Card>
    )
  }
}

VehicleListingFilterCard.defaultProps = {
  _id: null,
  ext: '.jpg',
  offer: false,
  reserved: false,
  recent: false,
  vatexempt: false,
  favourites: null,
  finance: {},
  onRecentFavourite: () => {},
  currency: { code: 'GBP', symbol: '£' }
}

VehicleListingFilterCard.propTypes = {
  id: string,
  image: string,
  ext: string,
  brand: string,
  model: string,
  derivative: string,
  price: shape({
    total: number,
    installment: shape({ payment: number, plan: string, code: string })
  }),
  currency: shape({ code: string, symbol: string }),
  path: string,
  offer: bool,
  offerTitle: string,
  reserved: bool,
  recent: bool,
  vatexempt: bool,
  favourites: object,
  finance: object,
  onInfoOpen: func,
  onRecentFavourite: func
}
export default withFavouritesContext(VehicleListingFilterCard)
